import { Distribute, IconButton, SvgArrowBack, Text } from '@loomhq/lens';
import React, { useEffect, useRef } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import { EventsList, ViewerAvatar, ViewerName, ViewsList } from './components';

import styles from './styles.module.less';

const DetailsSummary = ({
  title,
  amount,
  backgroundColor = 'backgroundSecondary',
  className,
}) => (
  <div
    className={`flex py:small px:medium bgc:${backgroundColor} items:center ${className} radius:medium`}
  >
    <div className="grow:1">
      <Text>{title}</Text>
    </div>
    <Text fontWeight="medium" size="xlarge">
      {amount}
    </Text>
  </div>
);

const Details = ({ viewer, onBack, skipAnimation }) => {
  const ref = useRef();
  const {
    user = {},
    views = [],
    percentCompleted = 0,
    ctaClicks,
    events = [],
  } = viewer;

  // scroll the parent on mount and on resize
  // to slide to this element
  // scrollIntoView will also scroll vertically
  // ending up hidden by the header :/
  useEffect(() => {
    const el = ref?.current;
    const shrinkParent = () =>
      el?.parentElement?.classList.add(styles.shrinked);

    document?.documentElement?.scrollTo?.({ top: 0 });
    el?.parentElement?.scrollTo?.({
      left: el.offsetWidth,
      behavior: skipAnimation ? 'auto' : 'smooth',
    });

    if (skipAnimation) {
      shrinkParent();
    } else {
      // shrink after scrolling animation to avoid jank
      setTimeout(shrinkParent, 250);
    }

    const ro = new ResizeObserver(([el]) => {
      el?.target?.parentElement?.scrollTo({
        left: el?.contentRect?.width,
        behavior: 'smooth',
      });
    });

    ro.observe(el);

    return () => ro.disconnect();
  }, [skipAnimation]);

  return (
    <section className={styles.slideable} ref={ref}>
      <Distribute
        htmlTag="header"
        alignment="center"
        isSpread
        className={styles.detailsHeader}
      >
        <Distribute alignment="center" onClick={onBack} gap="small">
          <IconButton altText="Back" icon={<SvgArrowBack />} />
          <ViewerAvatar
            avatar={user.avatar}
            color={user.color}
            variant={user.variant}
          />
          <ViewerName name={user.name} />
        </Distribute>
      </Distribute>
      <div>
        <div className="mb:large">
          <DetailsSummary
            title="Video views"
            amount={views.length}
            backgroundColor="blurpleMedium"
          />
          <DetailsSummary
            className="mt:small"
            title="Completion rate"
            amount={percentCompleted != null ? `${percentCompleted}%` : '--'}
            backgroundColor="blueLight"
          />
          {Boolean(ctaClicks) && (
            <DetailsSummary
              className="mt:small test-call-to-action-clicks"
              title="Call-to-Action Clicks"
              amount={ctaClicks}
              backgroundColor="yellowLight"
            />
          )}
        </div>

        <div className="p:medium bgc:backgroundSecondary mb:large radius:medium">
          <div className="mb:medium">
            <Text fontWeight="medium">View Times</Text>
          </div>
          <ViewsList views={views} />
        </div>
      </div>

      <EventsList events={events} />
    </section>
  );
};

export default Details;
